import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { XCircle, Check, AlertTriangle, PhoneCall } from 'lucide-react';
import { useMotivosNegacaoLead } from '@/hooks/useMotivosNegacaoLead';
import { ACOES_CONTATO_LEAD, useTentativasContatoLead } from '@/hooks/useTentativasContatoLead';

const MOTIVOS_PADRAO = [
  'Cliente optou por concorrente',
  'Cliente não tem mais interesse no produto/serviço',
  'Cliente se recusa a enviar os dados corretos da titular',
  'Cliente não retorna contato para prosseguir',
  'Valor fora do orçamento',
  'Outro',
];

interface ModalJustificativaNegacaoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmar: (motivo: string, observacao?: string) => void;
  loading?: boolean;
  leadId?: string | null;
}

const MIN_FRASE = 15;

export function ModalJustificativaNegacao({
  open,
  onOpenChange,
  onConfirmar,
  loading = false,
  leadId = null,
}: ModalJustificativaNegacaoProps) {
  const { data: motivosCadastrados, isError: erroMotivos } = useMotivosNegacaoLead();
  const MOTIVOS = motivosCadastrados && !erroMotivos
    ? motivosCadastrados.filter(m => m.ativo).map(m => m.titulo)
    : MOTIVOS_PADRAO;
  const { data: tentativas = [], isLoading: carregandoTentativas } = useTentativasContatoLead(leadId, open);
  const contagem = tentativas.reduce<Record<string, number>>((acc, t) => {
    acc[t.acao] = (acc[t.acao] || 0) + 1;
    return acc;
  }, {});
  const ligacoes = tentativas.filter(t => t.acao === 'LIGACAO_TENTADA' || t.acao === 'LIGACAO_REALIZADA');
  const semContato = !carregandoTentativas && tentativas.length === 0;
  const [motivoSelecionado, setMotivoSelecionado] = useState<string | null>(null);
  const [observacao, setObservacao] = useState('');

  const texto = observacao.trim();
  const podeConfirmar = !!motivoSelecionado && texto.length >= MIN_FRASE;

  const handleConfirmar = () => {
    if (!motivoSelecionado || texto.length < MIN_FRASE) return;
    const prefixoContato = semContato ? '[SEM CONTATO REGISTRADO] ' : '';
    onConfirmar(`${prefixoContato}${motivoSelecionado}: ${texto}`, texto);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setMotivoSelecionado(null);
      setObservacao('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" />
            Por que está negando este lead?
          </DialogTitle>
        </DialogHeader>

        {leadId && (
          <div
            className={cn(
              'rounded-lg border px-4 py-3 text-sm',
              semContato ? 'border-destructive bg-destructive/10 text-destructive' : 'border-border bg-muted/40'
            )}
          >
            {carregandoTentativas ? (
              <span className="text-muted-foreground">Conferindo tentativas de contato…</span>
            ) : semContato ? (
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Nenhuma tentativa de contato registrada neste lead</p>
                  <p className="text-xs">Nenhuma ligação, WhatsApp, e-mail, SMS ou proposta pelo CRM. Tente o contato antes de negar.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="font-medium flex items-center gap-1.5"><PhoneCall className="h-4 w-4" /> Tentativas de contato registradas</p>
                <p className="text-xs text-muted-foreground">
                  {Object.entries(contagem).map(([acao, n]) => `${ACOES_CONTATO_LEAD[acao] || acao}: ${n}`).join(' · ')}
                </p>
                {ligacoes.length === 0 ? (
                  <p className="text-xs font-medium text-destructive">Nenhuma ligação registrada.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Últimas ligações: {ligacoes.slice(0, 5).map(l => new Date(l.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })).join(' · ')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2 py-2">
          {MOTIVOS.map((motivo) => {
            const ativo = motivoSelecionado === motivo;
            return (
              <button
                key={motivo}
                onClick={() => setMotivoSelecionado(motivo)}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-lg border text-sm transition-all',
                  'flex items-center justify-between gap-2',
                  ativo
                    ? 'border-destructive bg-destructive/10 text-destructive font-medium'
                    : 'border-border hover:border-muted-foreground hover:bg-accent/50'
                )}
              >
                <span>{motivo}</span>
                {ativo && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Escreva o que aconteceu com o cliente <span className="text-destructive">*</span>
          </label>
          <Textarea
            placeholder="Pelo menos uma frase: o que o cliente disse, quando e por qual canal..."
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={3}
            className="resize-none"
          />
          {texto.length < MIN_FRASE && (
            <p className="text-xs text-muted-foreground">Mínimo {MIN_FRASE} caracteres ({texto.length}/{MIN_FRASE}).</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmar}
            disabled={!podeConfirmar || loading}
          >
            {loading ? 'Negando...' : 'Confirmar Negação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
